import {
  Button,
  Checkbox,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useLoaderData, useParams } from "react-router-dom";
import { get } from "../../../apiClient";
import TablePagination from "../../../components/TablePagination";
import CreativeRow from "../../../utils/creativeRow";
import {
  action,
  fireUpdateEmail,
  isUrlGoodToSave,
  REJECTION_REASON_KEY,
  URL_STATUS_KEY,
} from "../../../utils/urlsTabsHelper";
import BulkEditModal from "./BulkEditModal";
import {
  minimumSelectionHint,
  URL_NOUN,
  useBulkUrlSelection,
} from "./bulkUrlSelection";
import { BulkSelectionNotice, QueueSelectionControl } from "./SelectionBanner";

const DEFAULT_PAGINATION = { page: 0, perPage: 20 };
const DEFAULT_SEARCH_RESULT = {
  records: [],
  totalCount: 0,
  ...DEFAULT_PAGINATION,
};

const urlsTable = ({ campaignId, page, perPage, ...filters }) => {
  return new Promise((resolve, reject) => {
    const params = {
      page,
      perPage,
      ...filters,
    };
    get("managed_plus", `/campaigns/${campaignId}/campaign_urls`, { params })
      .then(({ result, total_count }) => {
        resolve({ records: result, totalCount: total_count, page, perPage });
      })
      .catch(reject);
  });
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: "#1976D214",
  },
}));

const ErrorRow = ({ error }) => {
  return (
    <StyledTableRow>
      <TableCell colSpan={7} sx={{ textAlign: "center" }}>
        {error}
      </TableCell>
    </StyledTableRow>
  );
};

const CreativeTable = ({
  searchOptions = {},
  refreshMethod,
  refreshFlag,
  enableEmailButtonMethod,
  enableEmailButtonVar,
  onForbiddenError,
  campaignStatus,
}) => {
  const [filters, setFilter] = useState({ ...DEFAULT_PAGINATION });
  const [isLoading, setIsLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(DEFAULT_SEARCH_RESULT);
  const isPendingUrl = searchOptions.status === "pending";
  const { page, perPage, records, error, totalCount } = searchResult;
  const { id: campaignId } = useParams();
  const { uid, account_email, episode_title, episode_link } = useLoaderData();
  const [openModalBulkEdit, setOpenModalBulkEdit] = useState(false);
  const [bulkEditParams, setBulkEditParams] = useState({});
  const [bulkSaveButtonEnabled, setBulkSaveButtonEnabled] = useState(false);
  const [justSentUpdateEmail, setJustSentUpdateEmail] = useState(false);
  const [emailButtonLabel, setEmailButtonLabel] = useState("SEND EMAIL UPDATE");
  const totalPages = Math.ceil(totalCount / perPage);
  const [reload, setReload] = useState(true);
  const selection = useBulkUrlSelection({
    records: records ?? [],
    totalCount: totalCount ?? 0,
    campaignId,
    status: searchOptions.status,
    noun: URL_NOUN,
    onForbiddenError,
  });

  useEffect(() => {
    if (page >= totalPages) {
      setFilter((prevFilters) => ({
        ...prevFilters,
        page: Math.max(totalPages - 1, 0),
      }));
    }
  }, [page, totalPages]);

  async function sendEmailUpdate() {
    try {
      await fireUpdateEmail(campaignId, account_email, uid);
      setJustSentUpdateEmail(true);
      setEmailButtonLabel("EMAIL UPDATE SENT");
    } catch (error) {
      if ("status" in error && error.status === 403) {
        onForbiddenError && onForbiddenError();
        return;
      }

      throw error;
    }
  }

  const updateURLBundle = () =>
    selection.save({
      params: bulkEditParams,
      campaignId,
      campaignStatus,
      onSuccess: () => {
        handleClose();
        refreshMethod();
        setReload(true);
      },
    });

  const bulkEditParameterUpdate = (parameter) => (e) => {
    const value = e;
    setBulkEditParams((prevParams) => {
      const updatedParams = {
        ...prevParams,
        [parameter]: value,
      };
      if (parameter === URL_STATUS_KEY && value !== "rejected") {
        updatedParams[REJECTION_REASON_KEY] = "";
      }
      setBulkSaveButtonEnabled(isUrlGoodToSave(updatedParams));
      return updatedParams;
    });
  };

  const updateURL = async (urlToEdit) => {
    let params = {
      id: urlToEdit.id,
      url_status_id: urlToEdit.status,
      url_content_rating_id: urlToEdit.url_content_rating_id,
    };

    if (urlToEdit.status === "rejected") {
      params = {
        ...params,
        rejection_reason: urlToEdit.rejection_reason,
      };
    }
    await action({
      urls_to_be_updated: JSON.stringify([params]),
      campaignId,
      campaign_status: campaignStatus,
    });
    selection.removeChecked(urlToEdit.id);
    if (isPendingUrl) refreshMethod();
    setReload(true);
  };

  const handleCheckboxClick = selection.toggleRow;

  useEffect(() => {
    if (!isPendingUrl) setReload(true);
  }, [isPendingUrl, reload, refreshFlag]);

  useEffect(() => {
    setIsLoading(true);
    if (reload) {
      urlsTable({ campaignId, ...searchOptions, ...filters })
        .then((result) => {
          setSearchResult(result);
          if (isPendingUrl) enableEmailButtonMethod(result.totalCount > 0);
        })
        .catch((error) => {
          if (error.status === 403) {
            onForbiddenError && onForbiddenError();
          }

          setSearchResult({
            error:
              "An error occurred while fetching the data. Please, contact support if the error is persistent.",
          });
        });
    }
    setIsLoading(false);
    setReload(false);
    return () => {};
  }, [
    campaignId,
    filters,
    searchOptions,
    refreshFlag,
    isPendingUrl,
    enableEmailButtonMethod,
    reload,
    onForbiddenError,
  ]);

  function handleClose() {
    setOpenModalBulkEdit(false);
    setBulkEditParams({});
    setBulkSaveButtonEnabled(false);
    selection.clearSaveError();
  }

  return (
    <>
      <Typography variant="h6">
        {isPendingUrl ? "Pending review" : "Reviewed"}
      </Typography>
      <BulkSelectionNotice
        notice={selection.notice}
        onClose={selection.dismissNotice}
      />
      <Grid
        container
        sx={{ px: 2, py: 1.5, alignItems: "center", columnGap: 1 }}
      >
        {isPendingUrl ? (
          <Tooltip
            title={
              selection.selectedCount < 2 ? minimumSelectionHint(URL_NOUN) : ""
            }
          >
            <span>
              <Button
                variant="contained"
                disabled={selection.selectedCount < 2}
                onClick={() => setOpenModalBulkEdit(true)}
              >
                {selection.buttonLabel}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            disabled={enableEmailButtonVar || justSentUpdateEmail}
            onClick={(e) => sendEmailUpdate()}
          >
            {emailButtonLabel}
          </Button>
        )}
        {isPendingUrl && selection.showQueueControl && (
          <QueueSelectionControl
            label={selection.queueLabel}
            onSelectAll={selection.selectQueue}
            allSelected={selection.allPendingSelected}
            selecting={selection.selectingAll}
            error={selection.selectAllError}
            clearLabel={selection.clearLabel}
            onClear={selection.clearSelection}
            selectedCount={selection.selectedCount}
          />
        )}
      </Grid>
      <TableContainer>
        {isLoading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              {isPendingUrl && totalCount > 0 ? (
                <TableCell>
                  <Checkbox
                    style={{ width: 20, height: 20 }}
                    checked={selection.headerChecked}
                    indeterminate={selection.headerIndeterminate}
                    onClick={selection.togglePage}
                    inputProps={{ "aria-label": selection.headerLabel }}
                  />
                </TableCell>
              ) : null}
              <TableCell>Content</TableCell>
              <TableCell
                style={{ width: 103, height: 36, padding: "6px 10px" }}
              >
                Content Type
              </TableCell>
              <TableCell style={{ width: 103, padding: "6px 10px" }}>
                Status
              </TableCell>
              <TableCell style={{ width: 322, padding: "6px 16px 6px 10px" }}>
                {isPendingUrl ? "Content Rating" : "Content Category"}
              </TableCell>
              <TableCell style={{ width: 158, padding: "6px 24px 6px 10px" }}>
                Update Status
              </TableCell>
              <TableCell style={{ width: 240, padding: "6px 10px" }}>
                Rejection Reason
              </TableCell>
              <TableCell
                style={{ width: 57, padding: "6px 10px" }}
                align="right"
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {error ? (
              <ErrorRow error={error} />
            ) : (
              records.map((item) => (
                <CreativeRow
                  onCheck={handleCheckboxClick}
                  onSave={updateURL}
                  checked={selection.isChecked(item.id)}
                  checkable={item.status === "pending"}
                  loading={isLoading}
                  urlInfo={item}
                  key={item.id}
                  episode_link={episode_link}
                  episode_title={episode_title}
                />
              ))
            )}
          </TableBody>
        </Table>
        {!error && (
          <TablePagination
            totalCount={totalCount}
            filters={filters}
            setFilter={setFilter}
          />
        )}
      </TableContainer>
      <BulkEditModal
        open={openModalBulkEdit}
        onClose={handleClose}
        onSave={updateURLBundle}
        onParamChange={bulkEditParameterUpdate}
        saveEnabled={bulkSaveButtonEnabled}
        params={bulkEditParams}
        selectedCount={selection.selectedCount}
        noun={URL_NOUN}
        saving={selection.saving}
        error={selection.saveError}
      />
    </>
  );
};

export default CreativeTable;
